import { IfAbility } from '@efdigital/ll-addon-auth';
import { EditContext } from '@efdigital/ll-admin-v2/components/edit-context.gts';
import { EditLtiConsumingPlatform } from '@efdigital/ll-admin-v2/components/lti/edit-lti-consuming-platform.gts';
import { buildDeleteConsumingPlatform } from '@efdigital/ll-admin-v2/data/builders/lti/lti-consuming-platforms';
import type { LibraryClientsEditLtiIntegrationsPlatformModel } from '@efdigital/ll-admin-v2/routes/library/clients/client/edit/lti-integrations/platform';
import { alertLtiDeleteError } from '@efdigital/ll-admin-v2/utils/lti/delete-errors';
import { on } from '@ember/modifier';
import type RouterService from '@ember/routing/router-service';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import type { Store } from '@warp-drive/core';

interface Signature {
  Args: {
    model: LibraryClientsEditLtiIntegrationsPlatformModel;
  };
}

export default class LtiIntegrationsPlatformIndexTemplate extends Component<Signature> {
  @service declare router: RouterService;
  @service declare store: Store;

  @tracked private _deleting = false;

  isDeleteDisabled = (isEditing: boolean): boolean =>
    isEditing || this._deleting;

  deletePlatform = async () => {
    if (
      !confirm(
        'Are you sure you want to delete this LMS platform? Delete all tools for this platform first.'
      )
    ) {
      return;
    }

    this._deleting = true;
    try {
      await this.store.request(
        buildDeleteConsumingPlatform(this.args.model.platform)
      );
      await this.router.transitionTo(
        'library.clients.client.edit.lti-integrations.index'
      );
    } catch (error) {
      alertLtiDeleteError(error);
    } finally {
      this._deleting = false;
    }
  };

  <template>
    <div data-test-lti-phase-platform>
      <EditContext as |context|>
        <div class="card">
          <div class="card-body">
            <EditLtiConsumingPlatform
              @item={{@model.platform}}
              @context={{context}}
              @initialState="readonly"
            />
          </div>
        </div>

        <IfAbility @ability="lti-integrations" @atLeast="editor">
          <:if>
            <div class="card border-danger mt-4">
              <div class="card-body">
                <h3 class="h6 text-danger mb-2">Delete platform</h3>
                <p class="text-muted small mb-3">
                  Permanently remove this LMS platform. Delete all deployed
                  tools and disconnect Xceed first.
                </p>
                <button
                  type="button"
                  class="btn btn-outline-danger"
                  disabled={{this.isDeleteDisabled context.isEditing}}
                  data-test-lti-delete-platform
                  {{on "click" this.deletePlatform}}
                >
                  {{#if this._deleting}}
                    Deleting…
                  {{else}}
                    <iconify-icon inline icon="ph:trash" />
                    Delete platform
                  {{/if}}
                </button>
              </div>
            </div>
          </:if>
        </IfAbility>
      </EditContext>
    </div>
  </template>
}
